import { AnalyticsEventNameType, FeatureFlag } from "@coeff/api";
import { useFeatureGate } from "@statsig/react-bindings";
import React, { useEffect, useRef, useState } from "react";

import { ThemePaletteIcon } from "../../components";
import { useApiContext, useTrack } from "../../utils";

import { fileToBase64 } from "./fileToBase64";
import {
  clearThemeRequestDraft,
  readThemeRequestDraft,
  writeThemeRequestDraft,
} from "./themeRequestDraft";

interface Props {
  /** Scopes the draft that survives a page refresh. */
  dashboardId?: string;
  /**
   * Changes when the user clicks "+ Custom" again while this card is already open — the click
   * needs an answer, so the description field takes focus (the sidebar handles scrolling).
   */
  focusNonce?: number;
  /**
   * Fired after the request is stored: the parent hands it to the editor agent and records a
   * receipt in the transcript, then unmounts this card.
   */
  onSubmitted: (submitted: {
    userRequestId: string;
    description: string;
    summary: string;
    tags: string[];
    /** Phrased as the user's own ask, for the chat message that hands off to the agent. */
    visibleRequest: string;
  }) => void;
  /** Close the card without submitting. */
  onDismiss: () => void;
}

/** What the theme pipeline can extract from (archives are unpacked to the best logo/PDF). */
const ACCEPTED_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "pdf", "zip", "pptx"];
const ACCEPT_ATTR = ".png,.jpg,.jpeg,.webp,.pdf,.zip,.pptx";
const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const IMAGE_EXTENSIONS = ["png", "jpg", "jpeg", "webp"];

const extensionOf = (filename: string): string =>
  filename.includes(".") ? filename.split(".").pop()!.toLowerCase() : "";

const formatSize = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

/** Extension badge color, matching how the file reads: document type vs. image. */
const badgeClassFor = (ext: string): string => {
  if (ext === "pdf") {
    return "bg-[#d92d20]";
  }
  if (ext === "pptx") {
    return "bg-[#c4520f]";
  }
  return "bg-gray-500";
};

const INPUT_FOCUS =
  "focus:border-[#3053ff] focus:ring-[3px] focus:ring-[#3053ff]/20 focus:outline-none";

/**
 * In-chat form for a custom theme request: description + brand website + one brand file.
 * Submitting stores a temporary custom-theme request (the agent's `create_custom_theme` reads it
 * back via `user_request_id`); the parent then closes this card and records a transcript receipt.
 *
 * One attachment only — `build_spec_from_brand_inputs` takes a single asset, and a ZIP/PPTX is
 * the path for several logos.
 */
export const ChatThemeRequestCard: React.FC<Props> = ({
  dashboardId,
  focusNonce = 0,
  onSubmitted,
  onDismiss,
}) => {
  const { apiClient } = useApiContext();
  const track = useTrack();
  const { value: isCustomThemeAgentEnabled } = useFeatureGate(
    FeatureFlag.EnableWebDashboardCustomThemeCreation
  );

  const [description, setDescription] = useState(
    () => readThemeRequestDraft(dashboardId)?.description ?? ""
  );
  const [brandUrl, setBrandUrl] = useState(
    () => readThemeRequestDraft(dashboardId)?.brandUrl ?? ""
  );
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const descriptionRef = useRef<HTMLTextAreaElement | null>(null);

  // Focus on mount and on every repeat "+ Custom" click.
  useEffect(() => {
    descriptionRef.current?.focus();
  }, [focusNonce]);

  // Local preview for image attachments; documents get an extension badge instead.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!file || !IMAGE_EXTENSIONS.includes(extensionOf(file.name))) {
      setPreviewUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  useEffect(() => {
    writeThemeRequestDraft(dashboardId, { description, brandUrl });
  }, [dashboardId, description, brandUrl]);

  const hasAnyInput = Boolean(description.trim() || brandUrl.trim() || file);

  const acceptFile = (candidate: File) => {
    if (!ACCEPTED_EXTENSIONS.includes(extensionOf(candidate.name))) {
      setFileError(`${candidate.name} isn’t a supported file. Use PNG, JPG, PDF, ZIP or PPTX.`);
      return;
    }
    if (candidate.size > MAX_FILE_SIZE_BYTES) {
      setFileError("That file is over 20 MB. Try a smaller one.");
      return;
    }
    setFile(candidate);
    setFileError(null);
    setFormError(null);
  };

  const handleSubmit = async () => {
    if (!hasAnyInput) {
      setFormError("Add a description, a website, or a file to continue.");
      return;
    }
    setFormError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      // brand_url is its own request field so extraction never mistakes a URL the user merely
      // mentioned in their text for the brand site; the description copy of it is only for the
      // human-readable email on the non-agent path.
      const urlLine = brandUrl.trim() ? `\n\nBrand website: ${brandUrl.trim()}` : "";
      const emailBody =
        "Custom theme request from Web Dashboard Viewer:\n\n" + description.trim() + urlLine;
      const attachmentBase64 = file ? await fileToBase64(file) : undefined;

      const response = await apiClient.createUserRequest({
        create_user_request_request: {
          request_type: "web_dashboard_theme_request",
          request_subtype: "custom_theme",
          description: emailBody,
          brand_url: brandUrl.trim() || undefined,
          attachment_base64: attachmentBase64,
          attachment_filename: file?.name,
          attachment_content_type: file ? file.type || "application/octet-stream" : undefined,
        } as any,
      });

      // Its own event: a request changes no theme, and counting it as VibeDashThemeChanged
      // inflated theme-adoption numbers.
      track(AnalyticsEventNameType.VibeDashThemeRequested);

      clearThemeRequestDraft(dashboardId);
      onSubmitted({
        userRequestId: response.data.user_request_id,
        description: emailBody,
        summary: description.trim().split("\n")[0],
        visibleRequest: description.trim()
          ? description.trim()
          : brandUrl.trim()
          ? `Create a theme from ${brandUrl.trim()}`
          : file
          ? `Create a theme from ${file.name}`
          : "Create a theme from the brand details I just sent.",
        tags: [
          ...(description.trim() ? ["Description"] : []),
          ...(brandUrl.trim() ? [brandUrl.trim()] : []),
          ...(file ? [file.name] : []),
        ],
      });
    } catch (error) {
      // Preserve the user's entries so a retry costs them nothing.
      // eslint-disable-next-line no-console
      console.error("Failed to send custom theme request", error);
      setSubmitError("Couldn’t send that. Your entries are still here — try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const fileExt = file ? extensionOf(file.name) : "";

  return (
    // shrink-0: `overflow-hidden` zeroes a flex item's automatic minimum size, so without it the
    // card collapses to its border inside the sidebar's flex-col message list.
    <div className="shrink-0 overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
      <div className="flex items-start gap-2 px-3 pt-3">
        <ThemePaletteIcon
          width="18px"
          height="18px"
          fill="currentColor"
          className="mt-0.5 shrink-0 text-[#111215]"
        />
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold leading-tight text-[#111215]">
            {isCustomThemeAgentEnabled ? "Create a custom theme" : "Request custom theme"}
          </div>
          <div className="mt-0.5 text-xs leading-snug text-gray-500">
            {isCustomThemeAgentEnabled
              ? "Describe your brand, link your site, or drop a file — any one is enough."
              : "Tell us about your brand and our team will put the theme together."}
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            clearThemeRequestDraft(dashboardId);
            onDismiss();
          }}
          aria-label="Close"
          className="-mr-1 grid size-6 shrink-0 cursor-pointer place-items-center rounded text-gray-400 hover:bg-gray-100 hover:text-gray-900"
        >
          <span className="material-icons !text-[16px] leading-none">close</span>
        </button>
      </div>

      <div className="flex max-h-[46vh] flex-col gap-2.5 overflow-y-auto px-3 pb-3 pt-2.5">
        <div className="flex flex-col gap-1.5">
          <label
            htmlFor="theme-request-description"
            className="text-[11.5px] font-semibold text-gray-600"
          >
            {isCustomThemeAgentEnabled
              ? "Describe the theme"
              : "Describe the theme you’d like us to create"}
          </label>
          <textarea
            ref={descriptionRef}
            id="theme-request-description"
            value={description}
            onChange={e => {
              setDescription(e.target.value);
              setFormError(null);
            }}
            disabled={isSubmitting}
            placeholder="e.g. “Our brand colors are #E50914 and #221F1F, dark background”"
            className={`min-h-[62px] w-full resize-y rounded-lg border border-gray-200 px-2.5 py-2 text-[13px] leading-snug text-gray-900 placeholder:text-gray-400 disabled:opacity-60 ${INPUT_FOCUS}`}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="theme-request-url" className="text-[11.5px] font-semibold text-gray-600">
            Brand website <span className="font-medium text-gray-500">· optional</span>
          </label>
          <div
            className={`flex items-center overflow-hidden rounded-lg border border-gray-200 focus-within:border-[#3053ff] focus-within:ring-[3px] focus-within:ring-[#3053ff]/20 ${
              isSubmitting ? "opacity-60" : ""
            }`}
          >
            <span className="select-none pl-2.5 text-xs text-gray-500">https://</span>
            <input
              id="theme-request-url"
              type="text"
              value={brandUrl}
              onChange={e => {
                setBrandUrl(e.target.value);
                setFormError(null);
              }}
              disabled={isSubmitting}
              placeholder="acme.com"
              className="min-w-0 flex-1 border-none py-2 pl-0.5 pr-2.5 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-[11.5px] font-semibold text-gray-600">
            Brand file <span className="font-medium text-gray-500">· optional</span>
          </span>
          {file ? (
            <div className="flex items-center gap-2 rounded-lg border border-gray-200 px-2 py-1.5">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt=""
                  className="size-[30px] shrink-0 rounded-md object-cover"
                />
              ) : (
                <span
                  className={`grid size-[30px] shrink-0 place-items-center rounded-md text-[9px] font-extrabold text-white ${badgeClassFor(
                    fileExt
                  )}`}
                >
                  {fileExt.toUpperCase().slice(0, 4)}
                </span>
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[12.5px] font-semibold text-[#111215]">
                  {file.name}
                </span>
                <span className="block text-[11px] text-gray-500">
                  {formatSize(file.size)}
                  {!isSubmitting && (
                    <>
                      {" · "}
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="cursor-pointer font-semibold text-[#3053ff] hover:underline"
                      >
                        Replace
                      </button>
                    </>
                  )}
                </span>
              </span>
              {!isSubmitting && (
                <button
                  type="button"
                  onClick={() => {
                    setFile(null);
                    setFileError(null);
                  }}
                  aria-label={`Remove ${file.name}`}
                  className="grid size-[22px] shrink-0 cursor-pointer place-items-center rounded text-[15px] leading-none text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                >
                  ×
                </button>
              )}
            </div>
          ) : (
            <div
              role="button"
              tabIndex={0}
              aria-label="Add a brand file"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragEnter={e => {
                e.preventDefault();
                setIsDraggingOver(true);
              }}
              onDragOver={e => {
                e.preventDefault();
                setIsDraggingOver(true);
              }}
              onDragLeave={() => setIsDraggingOver(false)}
              onDrop={e => {
                e.preventDefault();
                setIsDraggingOver(false);
                const dropped = e.dataTransfer.files?.[0];
                if (dropped) {
                  acceptFile(dropped);
                }
              }}
              className={`flex cursor-pointer flex-col items-center gap-0.5 rounded-lg border-[1.5px] border-dashed px-2.5 py-3 text-center transition-colors focus:outline-none ${
                isDraggingOver
                  ? "border-[#3053ff] bg-[#eff4fe]"
                  : fileError
                  ? "border-[#f0a4a0] bg-[#fffafa]"
                  : "border-gray-300 bg-gray-50/60 hover:border-[#3053ff] hover:bg-[#eff4fe]"
              }`}
            >
              <span className="text-[17px] leading-none text-gray-500">⇪</span>
              <span className="text-[12.5px] font-semibold text-[#111215]">
                Drop a file or <span className="text-[#3053ff]">browse</span>
              </span>
              <span className="text-[11px] text-gray-500">
                Logo, brand guidelines, or a logo pack
              </span>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPT_ATTR}
            className="hidden"
            onChange={e => {
              const picked = e.target.files?.[0];
              if (picked) {
                acceptFile(picked);
              }
              e.target.value = "";
            }}
          />
          {fileError && <span className="text-[11.5px] text-[#d92d20]">{fileError}</span>}
          {formError && <span className="text-[11.5px] text-[#d92d20]">{formError}</span>}
        </div>
      </div>

      <div className="flex items-center gap-2.5 border-t border-gray-200 bg-gray-50/60 px-3 py-2.5">
        <span
          className={`flex-1 text-[11px] leading-tight ${
            submitError ? "text-[#d92d20]" : "text-gray-500"
          }`}
        >
          {submitError ?? "PNG, JPG, PDF, ZIP or PPTX · up to 20 MB"}
        </span>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting || !hasAnyInput}
          className="h-[30px] shrink-0 cursor-pointer rounded-md bg-[#3053ff] px-3 text-[12.5px] font-semibold text-white transition-colors hover:bg-[#2746d9] disabled:cursor-not-allowed disabled:opacity-40"
        >
          {isSubmitting
            ? isCustomThemeAgentEnabled
              ? "Creating…"
              : "Sending…"
            : submitError
            ? "Try again"
            : isCustomThemeAgentEnabled
            ? "Create theme"
            : "Request theme"}
        </button>
      </div>
    </div>
  );
};
